import { useTranslate } from "@similarweb/i18n-provider";
import { Checkbox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";
import type { ExtensionSettingsFieldKey } from "app/pages/sales-intelligence/modules/extension-management/types";
import { SettingsCard } from "app/pages/sales-intelligence/pages/management/components/SettingsCard/SettingsCard";
import {
    CRM_AUTO_OPEN_LABEL_KEY,
    DATA_ENRICHMENT_CRM_CARDS,
    GOOGLE_WORKSPACE_CARD,
    GOOGLE_WORKSPACE_CHILDREN,
    LINKEDIN_AUTO_OPEN_LABEL_KEY,
    LINKEDIN_CARD,
} from "app/pages/sales-intelligence/pages/management/constants/extensionSettingsConfig";
import type { CrmAutoOpenKey } from "app/pages/sales-intelligence/pages/management/constants/extensionSettingsConfig";
import type { ExtensionSettings } from "app/pages/sales-intelligence/pages/management/types/types";

import { SettingsSectionTitle } from "../SettingsSectionTitle/SettingsSectionTitle";

type DataEnrichmentSectionProps = {
    settings: ExtensionSettings;
    onToggleLinkedin: () => void;
    onToggleCrmCard: (key: (typeof DATA_ENRICHMENT_CRM_CARDS)[number]["key"]) => void;
    onToggleGoogleWorkspace: () => void;
    onToggleGoogleWorkspaceChild: (key: (typeof GOOGLE_WORKSPACE_CHILDREN)[number]["key"]) => void;
    onToggleAutoOpenLinkedin: () => void;
    onToggleAutoOpenCrm: (key: CrmAutoOpenKey) => void;
};

type AutoOpenCheckboxProps = {
    selected: boolean;
    labelKey: string;
    dataAutomation: string;
    onToggle: () => void;
};

const AutoOpenCheckbox = ({ selected, labelKey, dataAutomation, onToggle }: AutoOpenCheckboxProps) => {
    const { translate } = useTranslate();

    return (
        <Flex margin="4px 0 0 0">
            <Flex data-automation={dataAutomation}>
                <Checkbox
                    selected={selected}
                    label={translate(labelKey)}
                    onClick={onToggle}
                    size="sm"
                />
            </Flex>
        </Flex>
    );
};

export const DataEnrichmentSection = ({
    settings,
    onToggleLinkedin,
    onToggleCrmCard,
    onToggleGoogleWorkspace,
    onToggleGoogleWorkspaceChild,
    onToggleAutoOpenLinkedin,
    onToggleAutoOpenCrm,
}: DataEnrichmentSectionProps) => {
    const { translate } = useTranslate();

    const trackedToggle = (key: ExtensionSettingsFieldKey, toggle: () => void) => {
        extensionManagementTrackingService.trackSettingsToggle(key, settings[key]);
        toggle();
    };

    return (
        <Flex
            flexWrap="wrap"
            gap="8px"
            width="100%"
        >
            <SettingsSectionTitle
                title={translate("si.management.extension.settings.data_enrichment.title")}
                description={translate("si.management.extension.settings.data_enrichment.description")}
            />
            <SettingsCard
                icon={LINKEDIN_CARD.icon}
                title={translate(LINKEDIN_CARD.titleKey)}
                isSelected={settings.enableLinkedin}
                onToggle={() => trackedToggle("enableLinkedin", onToggleLinkedin)}
                dataAutomation="sales-intelligence-extension-settings-enableLinkedin"
            >
                {settings.enableLinkedin ? (
                    <AutoOpenCheckbox
                        selected={settings.autoOpenLinkedin}
                        labelKey={LINKEDIN_AUTO_OPEN_LABEL_KEY}
                        dataAutomation="sales-intelligence-extension-linkedin-auto-open"
                        onToggle={() => trackedToggle("autoOpenLinkedin", onToggleAutoOpenLinkedin)}
                    />
                ) : null}
            </SettingsCard>
            {DATA_ENRICHMENT_CRM_CARDS.map((card) => {
                const autoOpenKey = card.autoOpenKey;

                return (
                    <SettingsCard
                        key={card.key}
                        icon={card.icon}
                        title={translate(card.titleKey)}
                        isSelected={settings[card.key]}
                        onToggle={() => trackedToggle(card.key, () => onToggleCrmCard(card.key))}
                        dataAutomation={`sales-intelligence-extension-settings-${card.key}`}
                    >
                        {autoOpenKey && settings[card.key] ? (
                            <AutoOpenCheckbox
                                selected={settings[autoOpenKey]}
                                labelKey={CRM_AUTO_OPEN_LABEL_KEY}
                                dataAutomation={`sales-intelligence-extension-${card.key.replace("enable", "").toLowerCase()}-auto-open`}
                                onToggle={() => trackedToggle(autoOpenKey, () => onToggleAutoOpenCrm(autoOpenKey))}
                            />
                        ) : null}
                    </SettingsCard>
                );
            })}
            <SettingsCard
                key={GOOGLE_WORKSPACE_CARD.key}
                icon={GOOGLE_WORKSPACE_CARD.icon}
                title={translate(GOOGLE_WORKSPACE_CARD.titleKey)}
                isSelected={settings.enableGoogleWorkspace}
                onToggle={() => trackedToggle("enableGoogleWorkspace", onToggleGoogleWorkspace)}
                dataAutomation="sales-intelligence-extension-settings-enableGoogleWorkspace"
            >
                {settings.enableGoogleWorkspace
                    ? GOOGLE_WORKSPACE_CHILDREN.map((child) => (
                          <Flex
                              key={child.key}
                              margin="4px 0 0 0"
                          >
                              <Flex data-automation={`sales-intelligence-extension-google-${child.key}`}>
                                  <Checkbox
                                      selected={settings[child.key]}
                                      label={translate(child.titleKey)}
                                      onClick={() =>
                                          trackedToggle(child.key, () => onToggleGoogleWorkspaceChild(child.key))
                                      }
                                      size="sm"
                                  />
                              </Flex>
                          </Flex>
                      ))
                    : null}
            </SettingsCard>
        </Flex>
    );
};
